<template>
  <MyWorkTripleDropdownTooltip
    :projects="menu"
    rootMenuClass="breadcrumb-root-menu"
    secondMenuClass="breadcrumb-second-menu"
    thirdMenuClass="breadcrumb-third-menu"
  >
    <template #label>
      <BaseSubHeading
        size="large"
        weight="400"
        color="monochrome-05"
        className="header__breadcrumb"
      >
        /<span>My Projects</span>
      </BaseSubHeading>
    </template>

    <template #project="{ project }">
      <BaseSubHeading
        class="breadcrumb-menu-item"
        size="large"
        weight="400"
        color="monochrome-07"
      >
        <span class="project-label">{{ project.label }}</span>

        <icon
          width="6px"
          height="8px"
          data="@icon/triangle_right.svg"
          fill="false"
          :color="iconColor"
          class="selection__arrow"
        />
      </BaseSubHeading>
    </template>

    <template #team="{ team }">
      <BaseSubHeading
        class="breadcrumb-menu-item breadcrumb-menu-item-team"
        size="large"
        weight="400"
        color="monochrome-07"
      >
        <span class="team-label">
          <Avatar
            :src="team.icon"
            size="sm"
          />
          <span class="team-label__text">
            {{ team.label }}
          </span>
        </span>

        <icon
          width="6px"
          height="8px"
          data="@icon/triangle_right.svg"
          fill="false"
          :color="iconColor"
          class="selection__arrow"
        />
      </BaseSubHeading>
    </template>

    <template #sprint="{ sprints }">
      <template
        v-for="sprint in sprints"
        :key="sprint.id"
      >
        <RouterLink
          v-if="sprint.route"
          :to="sprint.route"
          class="breadcrumb-menu-item breadcrumb-menu-item__sprint"
        >
          <SprintLabel :sprint="sprint" />
        </RouterLink>
      </template>
    </template>
  </MyWorkTripleDropdownTooltip>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Getter } from "s-vuex-class"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import MyWorkTripleDropdownTooltip from "@/components/Dropdown/MyWorkTripleDropdownTooltip.vue"
import SprintLabel from "@/components/SprintLabel/index.vue"

import { Account, User } from "@/models"
import { Getters } from "@/store"

import Avatar from "../Avatar/index.vue"

type ProjectsMenu = {
  id: string
  label: string
  urlName: string
  teams: {
    id: string
    label: string
    urlName: string
    icon?: string | null
    sprints: {
      id: string
      status: string
      sequenceNumber: number
      route: string | null
      continuousSprint: boolean
    }[]
  }[]
}[]

@Options({
  name: "BreadcrumbsMyWorkBlock",
  components: {
    MyWorkTripleDropdownTooltip,
    BaseSubHeading,
    DropdownTooltip,
    Avatar,
    SprintLabel
  }
})
export default class BreadcrumbsMyWorkBlock extends Vue {
  @Getter(Getters.GET_ACCOUNT) readonly account: Account
  @Getter(Getters.GET_USER_CURRENT_PROFILE) user: User
  @Getter(Getters.THEME_IS_DARK) isDark: boolean

  get menu() {
    if (!this.user) return []

    const { teams = [], projects = [], sprints = [] } = this.user

    const sprintsByTeam = new Map<string, typeof sprints>()

    for (const sprint of sprints) {
      if (!sprintsByTeam.has(sprint.teamId)) {
        sprintsByTeam.set(sprint.teamId, [])
      }
      sprintsByTeam.get(sprint.teamId)!.push(sprint)
    }

    const teamsByProject = new Map<string, ProjectsMenu[number]["teams"]>()

    for (const team of teams) {
      const teamSprints = sprintsByTeam.get(team.id)?.filter(sprint => sprint.status === "STARTED")

      if (!teamSprints || teamSprints.length === 0) continue

      const projectIds = team.projectIds ?? []

      for (const projectId of projectIds) {
        if (!teamsByProject.has(projectId)) {
          teamsByProject.set(projectId, [])
        }
        let route: string | null = null
        const projectUrl = projects.find(p => p.id === projectId)?.urlName
        if (team.urlName && projectUrl) {
          route = `/${this.account.urlName}/${projectUrl}/${team.urlName}/backlog`
        }
        teamsByProject.get(projectId)!.push({
          id: team.id,
          label: team.name,
          urlName: team.urlName ?? "",
          icon: team.picture?.pictureUrl ?? null,
          sprints: teamSprints.map(sprint => {
            return {
              id: sprint.id,
              status: sprint.status,
              sequenceNumber: sprint.sequenceNumber,
              route,
              continuousSprint: sprint.continuousSprint
            }
          })
        })
      }
    }

    return projects
      .filter(project => teamsByProject.has(project.id))
      .map(project => ({
        id: project.id,
        label: project.name,
        urlName: project.urlName,
        teams: teamsByProject.get(project.id) ?? []
      }))
  }

  get iconColor() {
    return this.isDark ? "#AFB4C0" : "#81889B"
  }
}
</script>
